import type { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import { quoteOrTabRemoved as buildQuoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { combineLatest, merge, of, race } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import type { Action } from 'state/actions';
import type { DynamicBookingAcked } from 'state/dynamic/dynamicForm.action';
import { quoteResultReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonQuoteAction, QuoteAcked, QuoteResultFailed } from 'state/forms/actions/quote';
import type { EpicWithThunk, Thunk } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { IHttpClient } from 'utils/http.old/httpClient.model';
import { fatEventApiConnector } from 'utils/streaming/api';
import { dynamicQuoteResult } from 'utils/streaming/messages';
import type { Streaming } from 'utils/streaming/model';

function quoteStreamed(
  streaming: Streaming,
  oldHttpClient: IHttpClient,
  getNow: () => Date,
  frontReconciliationId: string,
  tabId: string,
): Observable<Action | Thunk<void>> {
  return dynamicQuoteResult(streaming).pipe(
    filter(event => event.reconciliationId === frontReconciliationId),
    mergeMap(lightEvent => {
      return fatEventApiConnector(oldHttpClient)
        .getFatEventWithTabId<QuoteResultDto<DynamicFormResult>>(lightEvent, tabId)
        .pipe(
          map(event => {
            return quoteResultReceivedThunk(tabId, lightEvent.quoteId!, event);
          }),
          catchError(() => {
            return of(
              commonQuoteAction.quoteResultFailed(
                'dynamic',
                tabId,
                lightEvent.quoteId!,
                { message: 'quote.errorMessages.TechnicalError' },
                getNow(),
              ),
            );
          }),
        );
    }),
  );
}

function quoteAcked(action$: Observable<Action>, frontReconciliationId: string) {
  return action$.pipe(
    ofType('QUOTE_ACKED'),
    filter(event => event.workspace === 'dynamic' && event.frontReconciliationId === frontReconciliationId),
  );
}

function quoteTimedOut(
  quoteAcked$: Observable<QuoteAcked>,
  getNow: () => Date,
  tabId: string,
  appScheduler?: SchedulerLike,
): Observable<Action | Thunk<void>> {
  return quoteAcked$.pipe(
    mergeMap(({ quoteId, estimatedQuotingDurationInMillis: timeoutMs }) => {
      return of(
        commonQuoteAction.quoteResultFailed(
          'dynamic',
          tabId,
          quoteId,
          {
            message: 'quote.errorMessages.responseTimeout',
            values: { timeInMillis: timeoutMs },
          },
          getNow(),
        ),
      ).pipe(delay(timeoutMs, appScheduler));
    }),
  );
}

export function waitBoth(
  quoteAcked$: Observable<QuoteAcked | DynamicBookingAcked>,
  quoteStreamed$: Observable<Action | Thunk<void>>,
): Observable<Action | Thunk<void>> {
  return combineLatest([quoteAcked$, quoteStreamed$]).pipe(
    map(([_, streamed]) => {
      return streamed;
    }),
  );
}

export function quoteOrTabRemoved(
  action$: Observable<Action>,
  quoteAcked$: Observable<QuoteAcked | DynamicBookingAcked>,
  tabId: string,
  state$: Observable<AppState>,
): Observable<Action | Thunk<void>> {
  return merge(
    tabRemoved(action$, tabId, state$),
    quoteAcked$.pipe(
      mergeMap(({ quoteId }) => {
        return buildQuoteOrTabRemoved(action$, tabId, quoteId, state$);
      }),
    ),
  ).pipe(take(1));
}

export function quoteResultEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, streaming, getNow }) =>
    action$.pipe(
      ofType('QUOTE_REQUESTED'),
      filter(action => action.workspace === 'dynamic'),
      mergeMap(({ tabId, frontReconciliationId }) => {
        const quoteStreamed$ = quoteStreamed(streaming, oldHttpClient, getNow, frontReconciliationId, tabId);
        const quoteAcked$ = quoteAcked(action$, frontReconciliationId);
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, quoteAcked$, tabId, state$);
        const quoteTimedOut$ = quoteTimedOut(quoteAcked$, getNow, tabId, appScheduler);
        const quoteResult$ = waitBoth(quoteAcked$, quoteStreamed$);

        return race(quoteResult$, quoteTimedOut$).pipe(takeUntil(quoteOrTabRemoved$), take(1));
      }),
    );
}

export function quoteResultFailed(action$: Observable<Action>, tabId: string): Observable<QuoteResultFailed> {
  return action$.pipe(
    ofType('QUOTE_RESULT_FAILED'),
    filter(action => action.workspace === 'dynamic' && action.tabId === tabId),
  );
}
